import { styled } from '@mui/material';
import { MainContext } from '../../../context';
import { useContext, useMemo } from 'react';
import { AGENT_PROFILE_URL, US_STATES } from '../../../constants/common';

const Section1Container = styled('div')(({ theme }) => ({
  display: 'flex',
  maxWidth: 1500,
  margin: '0 auto',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
  '& div.image': {
    flexBasis: '50%',
    img: {
      objectFit: 'cover',
      maxWidth: '100%',
      width: '100%',
      height: '100%',
      [theme.breakpoints.down('md')]: {
        display: 'none',
      },
    },
  },
  '& div.content': {
    backgroundColor: '#2F2F2F',
    flexBasis: '50%',
    flexShrink: 0,
    padding: '80px 48px',
    [theme.breakpoints.down('md')]: {
      padding: '80px 32px',
      textAlign: 'center',
    },
    img: {
      display: 'block',
      maxHeight: 400,
      maxWidth: 400,
      width: '100%',
      objectFit: 'cover',
      objectPosition: 'top center',
      margin: '0 auto',
      borderRadius: 8,
      marginBottom: 24,
      [theme.breakpoints.up('md')]: {
        display: 'none',
      },
    },
    h2: {
      color: '#EAEAEA',
      fontSize: 40,
      lineHeight: '48px',
      fontWeight: 700,
    },
    h4: {
      color: '#B7B7B7',
      fontSize: 24,
      lineHeight: '36px',
      fontWeight: 500,
      marginBottom: 64,
    },
    p: {
      fontSize: 24,
      lineHeight: '36px',
      color: '#EAEAEA',
      fontWeight: 500,
    },
    '& div.communities': {
      marginTop: 64,
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
    },
  },
}));

const Badge = styled('div')(() => ({
  padding: '8px 16px',
  backgroundColor: '#E5F1FD',
  borderRadius: 50,
  fontSize: 16,
}));

const CommunityCard = styled('div')(() => ({
  padding: '24px 32px',
  backgroundColor: 'white',
  borderRadius: 8,
  '& div': {
    display: 'flex',
    gap: 16,
    '& div.svg': {
      backgroundColor: '#5069AF',
      borderRadius: 100,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      width: 45,
      height: 45,
    },
    '& div': {
      display: 'block',
      '& p.container-title': {
        color: '#494949',
        fontSize: 16,
        lineHeight: '18px',
        fontWeight: 600,
        marginBottom: 16,
      },
      '& div.list': {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px 16px',
      },
    },
  },
}));

const SectionAgent = () => {
  const { subdomainAgent } = useContext(MainContext);
  const { first_name, last_name, photo, description, licenses, states_served, cities_served, communities_served } =
    subdomainAgent;

  const states = useMemo(() => US_STATES.find(({ abbreviation }) => abbreviation === states_served), [states_served]);

  return (
    <Section1Container>
      {photo && (
        <div className="image">
          <img src={AGENT_PROFILE_URL + photo} alt={first_name + ' ' + last_name} />
        </div>
      )}
      <div className="content">
        <img src={AGENT_PROFILE_URL + photo} alt={first_name + ' ' + last_name} />
        <h2>
          {first_name} {last_name}
        </h2>
        <h4> {licenses.filter(a => a.license != '').length  > 0  ? 'License No: '+licenses.filter(a => a.license != '').map(l => l.license +'-'+ l.state).join(', '): null}</h4>
        <p>{description}</p>
        <div className="communities">
          <CommunityCard>
            <div>
              <div className="svg">
                <svg xmlns="http://www.w3.org/2000/svg" width="17" height="21" viewBox="0 0 17 21" fill="none">
                  <path
                    d="M2.89 15.2707C3.16052 15.1941 3.45131 15.225 3.69838 15.3566C3.94546 15.4882 4.1286 15.7098 4.2075 15.9726C4.2864 16.2354 4.25461 16.5179 4.11911 16.7579C3.98362 16.9979 3.75552 17.1758 3.485 17.2524C2.95375 17.4031 2.57125 17.5621 2.32581 17.7066C2.57869 17.8542 2.97819 18.0193 3.53281 18.1731C4.76 18.5137 6.51631 18.7387 8.5 18.7387C10.4837 18.7387 12.24 18.5137 13.4672 18.1731C14.0229 18.0193 14.4213 17.8542 14.6742 17.7066C14.4298 17.5621 14.0473 17.4031 13.5161 17.2524C13.2498 17.1724 13.0266 16.994 12.8945 16.7556C12.7624 16.5171 12.732 16.2378 12.81 15.9779C12.8879 15.7179 13.0678 15.4981 13.311 15.3658C13.5542 15.2336 13.8411 15.1994 14.11 15.2707C14.8198 15.472 15.47 15.73 15.9694 16.0614C16.4316 16.37 17 16.9077 17 17.7066C17 18.5148 16.4177 19.0566 15.9481 19.3652C15.4403 19.6976 14.7762 19.9567 14.0505 20.1579C12.5864 20.5656 10.625 20.803 8.5 20.803C6.375 20.803 4.41363 20.5656 2.9495 20.1579C2.22381 19.9567 1.55975 19.6976 1.05187 19.3652C0.58225 19.0556 0 18.5148 0 17.7066C0 16.9077 0.568437 16.37 1.03062 16.0614C1.53 15.73 2.18025 15.472 2.89 15.2707ZM8.5 0.160156C10.6134 0.160156 12.6403 0.975731 14.1348 2.42746C15.6292 3.8792 16.4688 5.84817 16.4688 7.90123C16.4688 10.5518 14.9812 12.7069 13.4406 14.2386C12.8282 14.8413 12.1701 15.3985 11.4718 15.9055C10.8407 16.3658 9.39781 17.2287 9.39781 17.2287C9.12415 17.3798 8.81478 17.4593 8.5 17.4593C8.18522 17.4593 7.87585 17.3798 7.60219 17.2287C6.88581 16.8258 6.19338 16.3841 5.52819 15.9055C4.82964 15.3988 4.17149 14.8416 3.55937 14.2386C2.01875 12.7069 0.53125 10.5518 0.53125 7.90123C0.53125 5.84817 1.37081 3.8792 2.86524 2.42746C4.35967 0.975731 6.38656 0.160156 8.5 0.160156ZM8.5 5.83694C7.93641 5.83694 7.39591 6.05443 6.9974 6.44156C6.59888 6.82869 6.375 7.35375 6.375 7.90123C6.375 8.44871 6.59888 8.97377 6.9974 9.3609C7.39591 9.74803 7.93641 9.96552 8.5 9.96552C9.06359 9.96552 9.60409 9.74803 10.0026 9.3609C10.4011 8.97377 10.625 8.44871 10.625 7.90123C10.625 7.35375 10.4011 6.82869 10.0026 6.44156C9.60409 6.05443 9.06359 5.83694 8.5 5.83694Z"
                    fill="#EAEAEA"
                  />
                </svg>
              </div>
              <div>
                <p className="container-title">Serving the Following States</p>
                <div className="list">
                  <Badge>{states?.name}</Badge>
                </div>
              </div>
            </div>
          </CommunityCard>
          <CommunityCard>
            <div>
              <div className="svg">
                <svg xmlns="http://www.w3.org/2000/svg" width="17" height="21" viewBox="0 0 17 21" fill="none">
                  <path
                    d="M2.89 15.2707C3.16052 15.1941 3.45131 15.225 3.69838 15.3566C3.94546 15.4882 4.1286 15.7098 4.2075 15.9726C4.2864 16.2354 4.25461 16.5179 4.11911 16.7579C3.98362 16.9979 3.75552 17.1758 3.485 17.2524C2.95375 17.4031 2.57125 17.5621 2.32581 17.7066C2.57869 17.8542 2.97819 18.0193 3.53281 18.1731C4.76 18.5137 6.51631 18.7387 8.5 18.7387C10.4837 18.7387 12.24 18.5137 13.4672 18.1731C14.0229 18.0193 14.4213 17.8542 14.6742 17.7066C14.4298 17.5621 14.0473 17.4031 13.5161 17.2524C13.2498 17.1724 13.0266 16.994 12.8945 16.7556C12.7624 16.5171 12.732 16.2378 12.81 15.9779C12.8879 15.7179 13.0678 15.4981 13.311 15.3658C13.5542 15.2336 13.8411 15.1994 14.11 15.2707C14.8198 15.472 15.47 15.73 15.9694 16.0614C16.4316 16.37 17 16.9077 17 17.7066C17 18.5148 16.4177 19.0566 15.9481 19.3652C15.4403 19.6976 14.7762 19.9567 14.0505 20.1579C12.5864 20.5656 10.625 20.803 8.5 20.803C6.375 20.803 4.41363 20.5656 2.9495 20.1579C2.22381 19.9567 1.55975 19.6976 1.05187 19.3652C0.58225 19.0556 0 18.5148 0 17.7066C0 16.9077 0.568437 16.37 1.03062 16.0614C1.53 15.73 2.18025 15.472 2.89 15.2707ZM8.5 0.160156C10.6134 0.160156 12.6403 0.975731 14.1348 2.42746C15.6292 3.8792 16.4688 5.84817 16.4688 7.90123C16.4688 10.5518 14.9812 12.7069 13.4406 14.2386C12.8282 14.8413 12.1701 15.3985 11.4718 15.9055C10.8407 16.3658 9.39781 17.2287 9.39781 17.2287C9.12415 17.3798 8.81478 17.4593 8.5 17.4593C8.18522 17.4593 7.87585 17.3798 7.60219 17.2287C6.88581 16.8258 6.19338 16.3841 5.52819 15.9055C4.82964 15.3988 4.17149 14.8416 3.55937 14.2386C2.01875 12.7069 0.53125 10.5518 0.53125 7.90123C0.53125 5.84817 1.37081 3.8792 2.86524 2.42746C4.35967 0.975731 6.38656 0.160156 8.5 0.160156ZM8.5 5.83694C7.93641 5.83694 7.39591 6.05443 6.9974 6.44156C6.59888 6.82869 6.375 7.35375 6.375 7.90123C6.375 8.44871 6.59888 8.97377 6.9974 9.3609C7.39591 9.74803 7.93641 9.96552 8.5 9.96552C9.06359 9.96552 9.60409 9.74803 10.0026 9.3609C10.4011 8.97377 10.625 8.44871 10.625 7.90123C10.625 7.35375 10.4011 6.82869 10.0026 6.44156C9.60409 6.05443 9.06359 5.83694 8.5 5.83694Z"
                    fill="#EAEAEA"
                  />
                </svg>
              </div>
              <div>
                <p className="container-title">Serving Cities and Surrounding Towns</p>
                <div className="list">
                  {cities_served?.map((city) => (
                    <Badge key={city.state}>{city.name}</Badge>
                  ))}
                </div>
              </div>
            </div>
          </CommunityCard>
          <CommunityCard>
            <div>
              <div className="svg">
                <svg xmlns="http://www.w3.org/2000/svg" width="17" height="21" viewBox="0 0 17 21" fill="none">
                  <path
                    d="M2.89 15.2707C3.16052 15.1941 3.45131 15.225 3.69838 15.3566C3.94546 15.4882 4.1286 15.7098 4.2075 15.9726C4.2864 16.2354 4.25461 16.5179 4.11911 16.7579C3.98362 16.9979 3.75552 17.1758 3.485 17.2524C2.95375 17.4031 2.57125 17.5621 2.32581 17.7066C2.57869 17.8542 2.97819 18.0193 3.53281 18.1731C4.76 18.5137 6.51631 18.7387 8.5 18.7387C10.4837 18.7387 12.24 18.5137 13.4672 18.1731C14.0229 18.0193 14.4213 17.8542 14.6742 17.7066C14.4298 17.5621 14.0473 17.4031 13.5161 17.2524C13.2498 17.1724 13.0266 16.994 12.8945 16.7556C12.7624 16.5171 12.732 16.2378 12.81 15.9779C12.8879 15.7179 13.0678 15.4981 13.311 15.3658C13.5542 15.2336 13.8411 15.1994 14.11 15.2707C14.8198 15.472 15.47 15.73 15.9694 16.0614C16.4316 16.37 17 16.9077 17 17.7066C17 18.5148 16.4177 19.0566 15.9481 19.3652C15.4403 19.6976 14.7762 19.9567 14.0505 20.1579C12.5864 20.5656 10.625 20.803 8.5 20.803C6.375 20.803 4.41363 20.5656 2.9495 20.1579C2.22381 19.9567 1.55975 19.6976 1.05187 19.3652C0.58225 19.0556 0 18.5148 0 17.7066C0 16.9077 0.568437 16.37 1.03062 16.0614C1.53 15.73 2.18025 15.472 2.89 15.2707ZM8.5 0.160156C10.6134 0.160156 12.6403 0.975731 14.1348 2.42746C15.6292 3.8792 16.4688 5.84817 16.4688 7.90123C16.4688 10.5518 14.9812 12.7069 13.4406 14.2386C12.8282 14.8413 12.1701 15.3985 11.4718 15.9055C10.8407 16.3658 9.39781 17.2287 9.39781 17.2287C9.12415 17.3798 8.81478 17.4593 8.5 17.4593C8.18522 17.4593 7.87585 17.3798 7.60219 17.2287C6.88581 16.8258 6.19338 16.3841 5.52819 15.9055C4.82964 15.3988 4.17149 14.8416 3.55937 14.2386C2.01875 12.7069 0.53125 10.5518 0.53125 7.90123C0.53125 5.84817 1.37081 3.8792 2.86524 2.42746C4.35967 0.975731 6.38656 0.160156 8.5 0.160156ZM8.5 5.83694C7.93641 5.83694 7.39591 6.05443 6.9974 6.44156C6.59888 6.82869 6.375 7.35375 6.375 7.90123C6.375 8.44871 6.59888 8.97377 6.9974 9.3609C7.39591 9.74803 7.93641 9.96552 8.5 9.96552C9.06359 9.96552 9.60409 9.74803 10.0026 9.3609C10.4011 8.97377 10.625 8.44871 10.625 7.90123C10.625 7.35375 10.4011 6.82869 10.0026 6.44156C9.60409 6.05443 9.06359 5.83694 8.5 5.83694Z"
                    fill="#EAEAEA"
                  />
                </svg>
              </div>
              <div>
                <p className="container-title">Serving the Following Communities</p>
                <div className="list">
                  {communities_served?.map((community) => (
                    <Badge key={community.id} style={{ borderRadius: 8 }}>
                      {community.name}, {community.address.city}, {states.name}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </CommunityCard>
        </div>
      </div>
    </Section1Container>
  );
};

export default SectionAgent;
